import {
	BackgroundAttributes,
	BackgroundControl,
	SpacingAttributes,
	SpacingControl,
	TypographyAttributes,
	TypographyControl,
} from '@redfox-thank-you/control-groups';
import {
	BaseControl,
	ButtonGroup,
	ColorPicker,
} from '@redfox-thank-you/controls';
import { InspectorControls } from '@wordpress/block-editor';
import { PanelBody, ToggleControl } from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import React from 'react';

import { headingLevelOptions } from '../constants';
import type { SettingsProps } from './types';

/**
 * Customer Details block settings component (Inspector Controls).
 */
export const Settings: React.FC< SettingsProps > = ( {
	updateAttributes,
	getAttribute,
} ) => {
	// Helper functions for specific attribute types
	const updateTypography = (
		section: 'title' | 'labels' | 'blockText',
		typographyAttributes: Partial< TypographyAttributes >,
	) => {
		const stylePath =
			section === 'blockText' ? 'style.block.text' : `style.${ section }`;
		updateAttributes( stylePath, typographyAttributes );
	};

	const updateBackground = (
		backgroundAttributes: Partial< BackgroundAttributes >,
	) => {
		updateAttributes( 'style.block.background', backgroundAttributes );
	};

	const updateSpacing = (
		spacingAttributes: Partial< SpacingAttributes >,
	) => {
		updateAttributes( 'style.block.spacing', spacingAttributes );
	};

	// Extract style properties with defaults
	const blockTextStyle = getAttribute< TypographyAttributes >(
		'style.block.text',
		{},
	);
	const blockBackgroundStyle = getAttribute< BackgroundAttributes >(
		'style.block.background',
		{},
	);
	const blockSpacingStyle = getAttribute< SpacingAttributes >(
		'style.block.spacing',
		{},
	);
	const titleStyle = getAttribute< TypographyAttributes >(
		'style.title',
		{},
	);
	const labelsStyle = getAttribute< TypographyAttributes >(
		'style.labels',
		{},
	);
	const tableBorderColor = getAttribute( 'style.table.borderColor', '' );

	// Extract settings properties with defaults
	const titleLevel = getAttribute( 'settings.title.level', 2 );
	const showTitle = getAttribute( 'settings.title.show', true );
	const showCustomerName = getAttribute(
		'settings.display.customerName',
		true,
	);
	const showCustomerEmail = getAttribute(
		'settings.display.customerEmail',
		true,
	);
	const showCustomerPhone = getAttribute(
		'settings.display.customerPhone',
		true,
	);
	const showBillingAddress = getAttribute(
		'settings.display.billingAddress',
		true,
	);
	const showDeliveryAddress = getAttribute(
		'settings.display.deliveryAddress',
		true,
	);
	const tableStyleEnabled = getAttribute(
		'settings.layout.tableStyle',
		false,
	);

	// Default values for reset functionality
	const defaultValues = {
		titleLevel: 2,
		showTitle: true,
		showCustomerName: true,
		showCustomerEmail: true,
		showCustomerPhone: true,
		showBillingAddress: true,
		showDeliveryAddress: true,
		tableStyleEnabled: false,
		tableBorderColor: '',
	};

	return (
		<>
			<InspectorControls>
				<PanelBody
					title={ __( 'Title Settings', 'redfox-thank-you' ) }
					initialOpen={ true }
				>
					<BaseControl
						id="show-title"
						label={ __( 'Show Title', 'redfox-thank-you' ) }
						help={ __(
							'Display or hide the block title.',
							'redfox-thank-you',
						) }
						inline
						helpAsTooltip
						showResetButton={ true }
						hasValue={ showTitle !== defaultValues.showTitle }
						onReset={ () =>
							updateAttributes(
								'settings.title.show',
								defaultValues.showTitle,
							)
						}
					>
						<ToggleControl
							label=""
							checked={ showTitle }
							onChange={ ( value ) =>
								updateAttributes( 'settings.title.show', value )
							}
							__nextHasNoMarginBottom
						/>
					</BaseControl>
					{ showTitle && (
						<>
							<BaseControl
								id="heading-level"
								label={ __(
									'Heading Level',
									'redfox-thank-you',
								) }
								help={ __(
									'Choose the semantic heading level for SEO and accessibility.',
									'redfox-thank-you',
								) }
								showResetButton={ true }
								hasValue={
									titleLevel !== defaultValues.titleLevel
								}
								onReset={ () =>
									updateAttributes(
										'settings.title.level',
										defaultValues.titleLevel,
									)
								}
							>
								<ButtonGroup
									value={ titleLevel.toString() }
									options={ headingLevelOptions }
									onChange={ ( value ) =>
										updateAttributes(
											'settings.title.level',
											parseInt( value || '2' ),
										)
									}
									allowDeselect={ false }
								/>
							</BaseControl>
						</>
					) }
				</PanelBody>

				<PanelBody
					title={ __( 'Display Options', 'redfox-thank-you' ) }
					initialOpen={ false }
				>
					<BaseControl
						id="show-customer-name"
						label={ __( 'Show Customer Name', 'redfox-thank-you' ) }
						help={ __(
							'Display the customer name in the output.',
							'redfox-thank-you',
						) }
						inline
						showResetButton={ true }
						hasValue={
							showCustomerName !== defaultValues.showCustomerName
						}
						onReset={ () =>
							updateAttributes(
								'settings.display.customerName',
								defaultValues.showCustomerName,
							)
						}
					>
						<ToggleControl
							label=""
							checked={ showCustomerName }
							onChange={ ( value ) =>
								updateAttributes(
									'settings.display.customerName',
									value,
								)
							}
							__nextHasNoMarginBottom
						/>
					</BaseControl>
					<BaseControl
						id="show-customer-email"
						label={ __(
							'Show Customer Email',
							'redfox-thank-you',
						) }
						help={ __(
							'Display the customer email address.',
							'redfox-thank-you',
						) }
						inline
						showResetButton={ true }
						hasValue={
							showCustomerEmail !==
							defaultValues.showCustomerEmail
						}
						onReset={ () =>
							updateAttributes(
								'settings.display.customerEmail',
								defaultValues.showCustomerEmail,
							)
						}
					>
						<ToggleControl
							label=""
							checked={ showCustomerEmail }
							onChange={ ( value ) =>
								updateAttributes(
									'settings.display.customerEmail',
									value,
								)
							}
							__nextHasNoMarginBottom
						/>
					</BaseControl>
					<BaseControl
						id="show-customer-phone"
						label={ __(
							'Show Customer Phone',
							'redfox-thank-you',
						) }
						help={ __(
							'Display the customer phone number if available.',
							'redfox-thank-you',
						) }
						inline
						showResetButton={ true }
						hasValue={
							showCustomerPhone !==
							defaultValues.showCustomerPhone
						}
						onReset={ () =>
							updateAttributes(
								'settings.display.customerPhone',
								defaultValues.showCustomerPhone,
							)
						}
					>
						<ToggleControl
							label=""
							checked={ showCustomerPhone }
							onChange={ ( value ) =>
								updateAttributes(
									'settings.display.customerPhone',
									value,
								)
							}
							__nextHasNoMarginBottom
						/>
					</BaseControl>
					<BaseControl
						id="show-billing-address"
						label={ __(
							'Show Billing Address',
							'redfox-thank-you',
						) }
						help={ __(
							'Display the customer billing address.',
							'redfox-thank-you',
						) }
						inline
						showResetButton={ true }
						hasValue={
							showBillingAddress !==
							defaultValues.showBillingAddress
						}
						onReset={ () =>
							updateAttributes(
								'settings.display.billingAddress',
								defaultValues.showBillingAddress,
							)
						}
					>
						<ToggleControl
							label=""
							checked={ showBillingAddress }
							onChange={ ( value ) =>
								updateAttributes(
									'settings.display.billingAddress',
									value,
								)
							}
							__nextHasNoMarginBottom
						/>
					</BaseControl>
					<BaseControl
						id="show-delivery-address"
						label={ __(
							'Show Delivery Address',
							'redfox-thank-you',
						) }
						help={ __(
							'Display the delivery/shipping address.',
							'redfox-thank-you',
						) }
						inline
						showResetButton={ true }
						hasValue={
							showDeliveryAddress !==
							defaultValues.showDeliveryAddress
						}
						onReset={ () =>
							updateAttributes(
								'settings.display.deliveryAddress',
								defaultValues.showDeliveryAddress,
							)
						}
					>
						<ToggleControl
							label=""
							checked={ showDeliveryAddress }
							onChange={ ( value ) =>
								updateAttributes(
									'settings.display.deliveryAddress',
									value,
								)
							}
							__nextHasNoMarginBottom
						/>
					</BaseControl>
				</PanelBody>
			</InspectorControls>

			<InspectorControls group="styles">
				<TypographyControl
					title={ __( 'Text', 'redfox-thank-you' ) }
					initialOpen={ true }
					attributes={ blockTextStyle }
					setAttributes={ ( attrs ) =>
						updateTypography( 'blockText', attrs )
					}
				/>

				{ showTitle && (
					<TypographyControl
						title={ __( 'Title Text', 'redfox-thank-you' ) }
						initialOpen={ false }
						attributes={ titleStyle }
						setAttributes={ ( attrs ) =>
							updateTypography( 'title', attrs )
						}
						enabledControls={ {
							wordSpacing: true,
						} }
					/>
				) }

				<TypographyControl
					title={ __( 'Label Text', 'redfox-thank-you' ) }
					initialOpen={ false }
					attributes={ labelsStyle }
					setAttributes={ ( attrs ) =>
						updateTypography( 'labels', attrs )
					}
					enabledControls={ {
						textAlign: false,
					} }
				/>

				<BackgroundControl
					title={ __( 'Background', 'redfox-thank-you' ) }
					initialOpen={ false }
					responsive={ true }
					attributes={ blockBackgroundStyle }
					setAttributes={ updateBackground }
					enabledControls={ {
						backgroundColor: true,
						backgroundImage: true,
						backgroundGradient: true,
						backgroundPattern: false,
						backgroundOverlay: true,
						advancedOptions: true,
						responsive: true,
					} }
				/>

				<SpacingControl
					title={ __( 'Spacing', 'redfox-thank-you' ) }
					initialOpen={ false }
					attributes={ blockSpacingStyle }
					setAttributes={ updateSpacing }
					enabledControls={ {
						padding: true,
						margin: true,
						responsive: true,
						presets: true,
						boxModel: true,
						autoValues: true,
					} }
					units={ [ 'px', 'em', 'rem', '%' ] }
					min={ 0 }
					max={ 200 }
					allowReset={ true }
				/>

				<PanelBody
					title={ __( 'Table Style', 'redfox-thank-you' ) }
					initialOpen={ false }
				>
					<BaseControl
						id="table-style"
						label={ __( 'Table Style', 'redfox-thank-you' ) }
						help={ __(
							'Display customer details in table style format.',
							'redfox-thank-you',
						) }
						inline
						showResetButton={ true }
						hasValue={
							tableStyleEnabled !==
							defaultValues.tableStyleEnabled
						}
						onReset={ () =>
							updateAttributes(
								'settings.layout.tableStyle',
								defaultValues.tableStyleEnabled,
							)
						}
					>
						<ToggleControl
							label=""
							checked={ tableStyleEnabled }
							onChange={ ( value ) =>
								updateAttributes(
									'settings.layout.tableStyle',
									value,
								)
							}
							__nextHasNoMarginBottom
						/>
					</BaseControl>
					{ tableStyleEnabled && (
						<BaseControl
							id="table-border-color"
							label={ __(
								'Table Border Color',
								'redfox-thank-you',
							) }
							help={ __(
								'Choose a color for table borders.',
								'redfox-thank-you',
							) }
							inline
							showResetButton={ true }
							hasValue={
								tableBorderColor !==
								defaultValues.tableBorderColor
							}
							onReset={ () =>
								updateAttributes(
									'style.table.borderColor',
									defaultValues.tableBorderColor,
								)
							}
						>
							<ColorPicker
								value={ tableBorderColor }
								onChange={ ( color ) =>
									updateAttributes(
										'style.table.borderColor',
										color || '',
									)
								}
							/>
						</BaseControl>
					) }
				</PanelBody>
			</InspectorControls>
		</>
	);
};
